/**
 * Brand configuration for the client portal. Defaults are the Crew brand with the self-hosted assets
 * in `public/brand/`; every value can be overridden at build time:
 *   VITE_BRAND_NAME           product name (the team app)       default "Crew"
 *   VITE_PORTAL_NAME          what clients see                  default "Crew Portal"
 *   VITE_BRAND_LOGO_URL       logo for the light theme          default /brand/crew-logo-light.svg
 *   VITE_BRAND_LOGO_DARK_URL  logo for the dark theme           default /brand/crew-logo-dark.svg
 *   VITE_SOURCE_CODE_URL      public source code (AGPL-3.0 §13) default the project repository
 * An organization's own logo (portal settings) still takes precedence where the UI supports it.
 */

export const DEFAULT_BRAND_NAME = 'Crew';
export const DEFAULT_PORTAL_NAME = 'Crew Portal';
export const DEFAULT_SOURCE_CODE_URL = 'https://github.com/MHasnainJafri/agentic-management-portal';

/** Upstream project this product is based on; only used for the license attribution. */
export const UPSTREAM_PROJECT_NAME = 'Worklenz';
export const UPSTREAM_LICENSE = 'AGPL-3.0';

const assetUrl = (path: string): string => `${import.meta.env.BASE_URL ?? '/'}${path}`;

const readEnv = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const customLogoUrl = readEnv(import.meta.env.VITE_BRAND_LOGO_URL);
const customLogoDarkUrl = readEnv(import.meta.env.VITE_BRAND_LOGO_DARK_URL);

export const BRAND_NAME: string = readEnv(import.meta.env.VITE_BRAND_NAME) ?? DEFAULT_BRAND_NAME;
export const PORTAL_NAME: string = readEnv(import.meta.env.VITE_PORTAL_NAME) ?? DEFAULT_PORTAL_NAME;

export const BRAND_LOGO_URL: string = customLogoUrl ?? assetUrl('brand/crew-logo-light.svg');
export const BRAND_LOGO_DARK_URL: string =
  customLogoDarkUrl ?? customLogoUrl ?? assetUrl('brand/crew-logo-dark.svg');

export const SOURCE_CODE_URL: string =
  readEnv(import.meta.env.VITE_SOURCE_CODE_URL) ?? DEFAULT_SOURCE_CODE_URL;

/** Picks the logo for a theme mode. */
export const brandLogoFor = (themeMode: string | undefined): string =>
  themeMode === 'dark' ? BRAND_LOGO_DARK_URL : BRAND_LOGO_URL;
